/** Connect Express account that can accept charges (includes legacy rows missing connectionMode). */
export function isStripeConnectExpressReady(stripe) {
    const accountId = String(stripe.connectedAccountId || '').trim();
    if (!accountId)
        return false;
    const chargesReady = stripe.connectChargesEnabled === true || stripe.connectOnboardingStatus === 'complete';
    if (!chargesReady)
        return false;
    if (stripe.connectionMode === 'byok')
        return false;
    return stripe.connectionMode === 'connect' || stripe.connectionMode == null;
}
/** True when Stripe can charge: BYOK secret key or completed Connect Express account. */
export function stripeHasCredentials(integrations) {
    const stripe = integrations.stripe;
    if (!stripe || stripe.isEnabled === false)
        return false;
    if (stripe.secretKey)
        return true;
    return isStripeConnectExpressReady(stripe);
}
export function helcimHasCredentials(integrations) {
    const helcim = integrations.helcim;
    return Boolean(helcim?.apiToken && helcim.isEnabled !== false);
}
/** True when Finix sub-merchant is approved and enabled. */
export function finixHasCredentials(integrations) {
    const finix = integrations.finix;
    if (!finix || finix.isEnabled === false)
        return false;
    if (!finix.merchantId)
        return false;
    return (finix.onboardingStatus === 'approved' ||
        finix.processingEnabled === true);
}
/** True when Helcim ACH is explicitly enabled and credentials exist. */
export function helcimAchEnabled(integrations) {
    return helcimHasCredentials(integrations) && integrations.helcim?.achEnabled === true;
}
/** True when Finix ACH is explicitly enabled and credentials exist. */
export function finixAchEnabled(integrations) {
    return finixHasCredentials(integrations) && integrations.finix?.achEnabled === true;
}
/** Collect.js accepts `xxxxxx-xxxxxx-xxxxxx-xxxxxx` or `checkout_public_` + 32 chars. */
const BANCCARD_TOKENIZATION_KEY_CLASSIC = /^.{6}-.{6}-.{6}-.{6}$/;
const BANCCARD_TOKENIZATION_KEY_PUBLIC = /^checkout_public_.{32}$/;
export function normalizeBanccardTokenizationKey(value) {
    return value
        .trim()
        .replace(/^["']+|["']+$/g, '')
        .replace(/[\u2010-\u2015\u2212\uFE58\uFE63\uFF0D]/g, '-')
        .replace(/[\u200B-\u200D\uFEFF]/g, '')
        .replace(/\s+/g, '');
}
export function isBanccardTokenizationKeyFormat(value) {
    const key = normalizeBanccardTokenizationKey(value);
    return BANCCARD_TOKENIZATION_KEY_CLASSIC.test(key) || BANCCARD_TOKENIZATION_KEY_PUBLIC.test(key);
}
/** True when BancCard BYOK keys are present and enabled. */
export function banccardHasCredentials(integrations) {
    const banccard = integrations.banccard;
    return Boolean(banccard?.securityKey &&
        banccard?.tokenizationKey &&
        banccard.isEnabled !== false);
}
/** True when BancCard ACH is explicitly enabled and credentials exist. */
export function banccardAchEnabled(integrations) {
    return banccardHasCredentials(integrations) && integrations.banccard?.achEnabled === true;
}
/**
 * Whether the active preferred card processor offers ACH for this business.
 * Stripe uses its own achEnabled flag; Helcim/Finix use theirs.
 */
export function isAchEnabledForPreferredProcessor(integrations) {
    if (!integrations)
        return false;
    const preferred = resolvePreferredCardProcessor(integrations);
    if (preferred === 'finix')
        return finixAchEnabled(integrations);
    if (preferred === 'helcim')
        return helcimAchEnabled(integrations);
    if (preferred === 'banccard')
        return banccardAchEnabled(integrations);
    if (preferred === 'stripe') {
        const stripe = integrations.stripe;
        return stripe?.achEnabled === true && stripeHasCredentials(integrations);
    }
    return false;
}
/** Resolve which card processor is active for a business. */
export function resolvePreferredCardProcessor(integrations) {
    if (!integrations)
        return null;
    const preferred = integrations.preferredCardProcessor;
    if (preferred === 'finix' && finixHasCredentials(integrations)) {
        return 'finix';
    }
    if (preferred === 'helcim' && helcimHasCredentials(integrations)) {
        return 'helcim';
    }
    if (preferred === 'banccard' && banccardHasCredentials(integrations)) {
        return 'banccard';
    }
    if (preferred === 'stripe' && stripeHasCredentials(integrations)) {
        return 'stripe';
    }
    const active = [];
    if (finixHasCredentials(integrations))
        active.push('finix');
    if (helcimHasCredentials(integrations))
        active.push('helcim');
    if (banccardHasCredentials(integrations))
        active.push('banccard');
    if (stripeHasCredentials(integrations))
        active.push('stripe');
    if (active.length === 1)
        return active[0];
    if (active.length > 1) {
        // Prefer explicit preference when somehow multiple are enabled; else stripe then helcim then finix then banccard
        if (preferred && active.includes(preferred))
            return preferred;
        if (active.includes('stripe'))
            return 'stripe';
        if (active.includes('helcim'))
            return 'helcim';
        if (active.includes('finix'))
            return 'finix';
        return 'banccard';
    }
    return null;
}
export function isCardProcessorConfigured(integrations) {
    return resolvePreferredCardProcessor(integrations) != null;
}
/** Processors that use ServiceFlow /pay/select links instead of Stripe-hosted links. */
export function isSelectionLinkCardProcessor(processor) {
    return processor === 'helcim' || processor === 'finix' || processor === 'banccard';
}
//# sourceMappingURL=paymentProcessor.js.map